import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import toast from "react-hot-toast";

interface Tenant {
  name?: string;
  document?: File;
  // Add other fields as needed
}

// Set default values
const defaultTenant: Tenant = {
  name: "Default Name",
  // Add default values for other fields as needed
};
interface Tenants {
  [key: string]: string | number | File; // Define types for possible Tenant fields
}

// Hook to manage tenants data
export function useTenants() {
  const queryClient = useQueryClient();
    const toastId = "expense-toast";

  // // GET all tenants
  // const _Tenants = (params?: { active: boolean,delete: boolean, page: number; size: number }) =>
  //   useQuery({
  //     queryKey: ["tenants"],
  //     queryFn: async () => {
  //       try {
  //         const response = await axios.get(`/api/v1/all_tenants`, {
  //           params: params,
  //         });
  //         return response.data as ApiResponse;
  //       } catch (error) {
  //         console.error("Error fetching tenants:", error);
  //         // throw error;
  //       }
  //     },
  //     staleTime: 30000,
  //   });
  // GET all tenants
const _Tenants = (params?: { active?: boolean, delete?: boolean, page: number; size: number }) =>
  useQuery({
    queryKey: ["tenants"],
    queryFn: async () => {
      try {
        const response = await axios.get(`/api/v1/all_tenants`, {
          params: {
            ...(params?.delete ? {} : { active: params?.active }), // Only include active if not deleted
            delete: params?.delete,
            page: params?.page,
            size: params?.size
          }
        });
        return response.data as ApiResponse;
      } catch (error) {
        console.error("Error fetching tenants:", error);
      }
    },
    staleTime: 30000,
  });

    const addTenant = useMutation({
      mutationFn: async (newTenant: Tenant) => {
        const tenantData: Tenant = { ...defaultTenant, ...newTenant };
        const formData = new FormData();
    
        Object.keys(tenantData).forEach((key) => {
          const value = tenantData[key as keyof Tenant];
          // if (value !== undefined) {
            if (value !== undefined && value !== null) { // check the value not null
            if (value instanceof File) {
              formData.append(key, value);
            } else {
              // formData.append(key, value.toString());
              formData.append(key, String(value)); // String() to handle null
            }
          }
        });
    
        // Send formData with Axios
        const response = await axios.post(`/api/v1/add_tenant`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
    
        return response.data; // You can return the response data if needed
      },
   
      // onError: (error) => {
      //   toast.error(
      //     error.message
      //       ? `${error.message}`
      //       : "Sorry, Failed to add Tenant, please try again"
      //   );
      // },

      onError: (error: AxiosError) => { // Explicitly type error as AxiosError
    let errorMessage = "Sorry, Failed to add Tenant, please try again."; // Default message

    // Check if it's an Axios error and has a response with data and a message
    if (axios.isAxiosError(error) && error.response && error.response.data && (error.response.data as any).message) {
      errorMessage = (error.response.data as any).message;
    } else if (error.message) {
      // Fallback to the generic error message if no specific backend message
      errorMessage = error.message;
    }

    toast.error(errorMessage);
  },

   
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey:["users"]}); 
        toast.success("User  has successfully added a tenant...");
      },
    });

  // POST new tenant
  // const addTenant = useMutation({
  //   mutationFn: async (newTenant: any) => {
  //     try {
  //       const formData = new FormData();
  //     // Append each property of newTenant to formData
  //     Object.keys(newTenant).forEach((key) => {
  //       const value = newTenant[key];
  //       if (value !== undefined) {
  //         // Append value as a string or File depending on its type
  //         if (value instanceof File) {
  //           formData.append(key, value);
  //         } else {
  //           formData.append(key, value);
  //         }
  //       }
  //     });
  //       const response = await axios.post(`/api/v1/add_tenant`, newTenant);
  //       return response.data;
  //     } catch (error) {
  //       console.error("Error adding tenant:", error);
  //       throw error;
  //     }
  //   },
  //   // onSuccess: () => {
  //   //   queryClient.invalidateQueries(["tenants"]); // Refresh tenants data
  //   // },
  // });

  // GET tenant by ID
  const getTenantById = useMutation({
    mutationFn: async (id: string) => {
      try {
        const response = await axios.get(`/tenants/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching tenant by ID:", error);
        throw error;
      }
    },
  });

  // UPDATE tenant
  const updateTenant = useMutation({
    mutationFn: async ({
      id,
      updatedTenant,
    }: {
      id: any;
      updatedTenant: any;
    }) => {
      // Merge the defaultTenant with updatedTenant
      const tenantData: Tenant = { ...defaultTenant, ...updatedTenant };
  
      const formData = new FormData();
      // Iterate over keys to populate formData
      Object.keys(tenantData).forEach((key) => {
        const value = tenantData[key as keyof typeof tenantData];
        if (value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key, value); // Append file as-is
          } else {
            if (key !== "document" && key !== "tenantImage") {
              formData.append(key, value.toString()); // Convert to string
            }
          }
        }
      });
  
      const response = await axios.patch(
        `/api/v1/update_tenant/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
  
      return response.data; // Return the response data
    },
    // onError: (error) => {
    //   console.error("Error updating tenant:", error);
    //   toast.error(
    //     error.message
    //       ? `${error.message}`
    //       : "Sorry, Failed to update Tenant, please try again"
    //   );
    // },
    onError: (error: AxiosError) => { // Explicitly type error as AxiosError
    console.error("Error updating tenant:", error); // Keep console error for debugging

    let errorMessage = "Sorry, Failed to update Tenant, please try again."; // Default message

    // Check if it's an Axios error and has a response with data and a message
    if (axios.isAxiosError(error) && error.response && error.response.data && (error.response.data as any).message) {
      errorMessage = (error.response.data as any).message;
    } else if (error.message) {
      // Fallback to the generic error message if no specific backend message
      errorMessage = error.message;
    }

    toast.error(errorMessage);
  },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] }); // Invalidate cache
      toast.success("Tenant has been successfully updated.", { id: toastId });
    },
  });
  // DELETE tenant
  const deleteTenant = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/v1/permanent_delete_tenant/${id}`
      );
      return response.data; // Return the response data
    },
    onError: (error) => {
      console.error("Error deleting tenant:", error);
      toast.error(
        error.message
          ? `${error.message}`
          : "Sorry, Failed to delete Tenant, please try again"
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success("Tenant has been successfully deleted.");
    },
  });

  

  return {
    _Tenants,
    queryClient,
    addTenant,
    getTenantById,
    updateTenant,
    deleteTenant,
  };
}
